import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Box,
  Flex,
  HStack,
  Tag,
  TagLabel,
  Text,
  useColorModeValue,
  VStack,
  Icon,
  Collapse,
  Tooltip,
} from "@chakra-ui/react";
import { useUserPermissions } from "hooks/useUserPermissions";
import { useOptimizeAccess } from "hooks/useOptimizeAccess";
import GamelightButton from "components/button";

const useRouteLogic = () => {
  const location = useLocation();
  const activeRoute = (routeName: string) => {
    if (routeName === "/ua") {
      return (
        location.pathname.includes("/ua") ||
        location.pathname.includes("/ua/cpi/create") ||
        location.pathname.includes("/ua/roas/create")
      );
    }
    if (routeName === "/ua/cpi") {
      return (
        location.pathname.includes(routeName) ||
        location.pathname.includes("/ua/cpi/create")
      );
    }
    if (routeName === "/ua/roas") {
      return (
        location.pathname.includes(routeName) ||
        location.pathname.includes("/ua/roas/create")
      );
    }
    return location.pathname.includes(routeName);
  };
  return { activeRoute };
};

const useColorModes = () => {
  const activeColor = useColorModeValue("gray.700", "white");
  const inactiveColor = useColorModeValue(
    "secondaryGray.600",
    "secondaryGray.600"
  );
  const activeIcon = useColorModeValue("brand.500", "white");
  const textColor = useColorModeValue("secondaryGray.500", "white");
  const brandColor = useColorModeValue("brand.500", "brand.400");
  return { activeColor, inactiveColor, activeIcon, textColor, brandColor };
};

const SubLink: React.FC<{ route: RoutesType }> = ({ route }) => {
  const { activeRoute } = useRouteLogic();
  const { activeColor, textColor } = useColorModes();

  const isRoas = route.path === "/ua/roas";

  return (
    <NavLink to={route.path} style={{ textDecoration: "none" }}>
      <HStack>
        <Text
          ps="44px"
          py="5px"
          color={
            activeRoute(route.path.toLowerCase()) ? activeColor : textColor
          }
          fontWeight={activeRoute(route.path.toLowerCase()) ? "bold" : "normal"}
        >
          {route.name}
        </Text>
        {isRoas && (
          <Tag size="sm" colorScheme="orange" borderRadius="full">
            <TagLabel fontWeight={"bold"}>NEW</TagLabel>
          </Tag>
        )}
      </HStack>
    </NavLink>
  );
};

const DisabledSubLink: React.FC<{ route: RoutesType; message?: string }> = ({
  route,
  message,
}) => {
  const { activeRoute } = useRouteLogic();
  const { activeColor, textColor } = useColorModes();

  const isRoas = route.path === "/ua/roas";
  const isCpa = route.path === "/ua/cpa";
  return (
    <Tooltip label={message ?? ""} placement="top">
      <HStack cursor={"not-allowed"}>
        <Text
          ps="44px"
          py="5px"
          color={
            activeRoute(route.path.toLowerCase()) ? activeColor : textColor
          }
          fontWeight={activeRoute(route.path.toLowerCase()) ? "bold" : "normal"}
        >
          {route.name}
        </Text>
        {(isRoas || isCpa) && (
          <Tag size="sm" colorScheme="orange" borderRadius="full">
            <TagLabel fontWeight={"bold"}>NEW</TagLabel>
          </Tag>
        )}
      </HStack>
    </Tooltip>
  );
};

const MainLink: React.FC<{
  route: RoutesType;
}> = ({ route }) => {
  const { activeRoute } = useRouteLogic();
  const { activeColor, textColor, activeIcon, brandColor } = useColorModes();
  const showAMOnlyFlag =
    route.path === "/retention" || route.path === "/events";

  return (
    <Flex align="center" mb={2}>
      <NavLink
        to={route.path}
        style={{ textDecoration: "none", flex: 1 }}
        exact={!route.subRoutes}
      >
        <Flex
          gap={activeRoute(route.path.toLowerCase()) ? "22px" : "26px"}
          py="5px"
          ps="10px"
          px={"5px"}
          width="100%"
          alignItems={"center"}
        >
          <Flex w="100%" alignItems="center">
            {route.icon && (
              <Flex
                color={
                  activeRoute(route.path.toLowerCase()) ? activeIcon : textColor
                }
                me="18px"
              >
                {route.icon}
              </Flex>
            )}
            <Text
              color={
                activeRoute(route.path.toLowerCase()) ? activeColor : textColor
              }
              fontWeight={
                activeRoute(route.path.toLowerCase()) ? "bold" : "normal"
              }
            >
              {route.name}
              {showAMOnlyFlag && (
                <Tag size="sm" colorScheme="cyan" ml={1} borderRadius="full">
                  <TagLabel fontWeight={"bold"}>AM Only</TagLabel>
                </Tag>
              )}
            </Text>
          </Flex>
        </Flex>
      </NavLink>
    </Flex>
  );
};

const RouteLink: React.FC<{ route: RoutesType }> = ({ route }) => {
  const { isRoasCampaignsEnabled, isBiddingTypeEnabled } = useUserPermissions();

  return (
    <Box>
      <MainLink route={route} />
      {route.subRoutes && (
        <VStack align="stretch" mt={2} mb={4}>
          {route.subRoutes
            .filter((subRoute) => {
              if (subRoute.path === "/ua/cpa") {
                return isBiddingTypeEnabled;
              }
              return true;
            })
            .map((subRoute, index) => {
              if (subRoute.path === "/ua/roas" && !isRoasCampaignsEnabled) {
                return (
                  <DisabledSubLink
                    key={index}
                    route={subRoute}
                    message="Contact your Account Manager to enable ROAS campaigns"
                  />
                );
              }
              return <SubLink key={index} route={subRoute} />;
            })}
        </VStack>
      )}
    </Box>
  );
};

export function SidebarLinks({ routes }: { routes: RoutesType[] }) {
  const { isSpecialMode, isMain, isNotificationManagerEnabled } =
    useUserPermissions();
  const { hasOptimizeAccess } = useOptimizeAccess();

  const filteredRoutes = routes
    .filter((route) => route.icon)
    .filter((route) => {
      if (route.path === "/retention" && !isMain) return false;
      if (route.path === "/events" && !isSpecialMode) return false;
      if (route.path === "/optimize" && !hasOptimizeAccess) return false;
      if (
        route.path === "/notification-manager" &&
        !isNotificationManagerEnabled
      )
        return false;
      return route.layout === "/";
    });

  return (
    <>
      {filteredRoutes.map((route, index) => (
        <RouteLink key={index} route={route} />
      ))}
    </>
  );
}

export default SidebarLinks;
